/**
 * Module Configuration
 * Defines which modules support content editing and/or settings editing
 *
 * i18n keys are stored here to keep all module config centralized.
 * When adding a new module type, add all its config in one place.
 */

export const MODULE_CAPABILITIES = {
  text: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'richText',
    icon: 'TextFormat',
    label: 'Text',
    labelKey: 'editCourse.moduleType.text',
    i18n: {
      add: 'editCourse.addText',
      edit: 'editCourse.editTextContent',
    },
  },
  image: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'media',
    icon: 'Image',
    label: 'Image',
    labelKey: 'editCourse.moduleType.image',
    i18n: {
      add: 'editCourse.addImage',
      edit: 'editCourse.editImageContent',
    },
  },
  video: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'media',
    icon: 'Video',
    label: 'Video',
    labelKey: 'editCourse.moduleType.video',
    i18n: {
      add: 'editCourse.addVideo',
      edit: 'editCourse.editVideoContent',
    },
  },
  audio: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'media',
    icon: 'Audio',
    label: 'Audio',
    labelKey: 'editCourse.moduleType.audio',
    i18n: {
      add: 'editCourse.addAudio',
      edit: 'editCourse.editAudioContent',
    },
  },
  embed: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'embed',
    icon: 'Integration',
    label: 'Embed',
    labelKey: 'editCourse.moduleType.embed',
    i18n: {
      add: 'editCourse.addEmbed',
      edit: 'editCourse.editEmbedContent',
    },
  },
  task: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'task',
    icon: 'Task',
    label: 'Task',
    labelKey: 'editCourse.moduleType.task',
    i18n: {
      add: 'editCourse.addTask',
      edit: 'editCourse.editTaskContent',
    },
  },
  download: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'media',
    icon: 'Download',
    label: 'Download',
    labelKey: 'editCourse.moduleType.download',
    i18n: {
      add: 'editCourse.addDownload',
      edit: 'editCourse.editDownloadContent',
    },
  },
  discussion: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'discussion',
    icon: 'Forum',
    label: 'Discussion',
    labelKey: 'editCourse.moduleType.discussion',
    i18n: {
      add: 'editCourse.addDiscussion',
      edit: 'editCourse.editDiscussionContent',
    },
  },
  slideshow: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'slideshow',
    icon: 'PhotoLibrary',
    label: 'Slideshow',
    labelKey: 'editCourse.moduleType.slideshow',
    i18n: {
      add: 'editCourse.addSlideshow',
      edit: 'editCourse.editSlideshowContent',
    },
  },
  webinar: {
    hasContent: true,
    hasSettings: false,
    contentEditor: 'webinar',
    icon: 'Webinar',
    label: 'Webinar',
    labelKey: 'editCourse.moduleType.webinar',
    i18n: {
      add: 'editCourse.addWebinar',
      edit: 'editCourse.editWebinarContent',
    },
  },
  diploma: {
    hasContent: false,
    hasSettings: true,
    contentEditor: null,
    icon: 'Certificate',
    label: 'Diploma',
    labelKey: 'editCourse.moduleType.diploma',
    i18n: {
      add: 'editCourse.addDiploma',
      edit: 'editCourse.editDiplomaContent',
    },
  },
};

/**
 * Ordered list of module types for UI display (e.g., insertion zone)
 * Excludes diploma as it's typically added automatically
 */
export const MODULE_TYPES = ['text', 'image', 'video', 'audio', 'embed', 'task', 'download', 'discussion', 'slideshow', 'webinar'].map((type) => ({
  type,
  icon: MODULE_CAPABILITIES[type].icon,
  label: MODULE_CAPABILITIES[type].label,
  labelKey: MODULE_CAPABILITIES[type].labelKey,
}));

/**
 * Get module capabilities
 * @param {string} moduleType - The module type name
 * @returns {Object} Module capabilities
 */
export const getModuleCapabilities = (moduleType) => {
  return MODULE_CAPABILITIES[moduleType] || { hasContent: false, hasSettings: false };
};

/**
 * Check if module supports content editing
 * @param {string} moduleType - The module type name
 * @returns {boolean}
 */
export const hasContentEditor = (moduleType) => {
  return MODULE_CAPABILITIES[moduleType]?.hasContent || false;
};

/**
 * Check if module supports settings editing
 * @param {string} moduleType - The module type name
 * @returns {boolean}
 */
export const hasSettingsEditor = (moduleType) => {
  return MODULE_CAPABILITIES[moduleType]?.hasSettings || false;
};
